import { LogOut, LayoutDashboard, BookOpen, Users, CreditCard, FileText, Search, GraduationCap, Wrench, LayoutGrid, UserCircle, Bot } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar"

interface WorkspaceSidebarProps {
  onSignOut: () => void;
  pathname: string;
  plan?: string;
}

const WorkspaceSidebar = ({ onSignOut, pathname, plan = '' }: WorkspaceSidebarProps) => {
  const { isMobile, setOpenMobile } = useSidebar();
  const isTier2 = plan.toLowerCase().includes('tier2');

  const allLinks = [
    { to: "/dashboard", label: "OVERVIEW", icon: LayoutDashboard, requiredTier: 1 },
    { to: "/dashboard/lessons", label: "LESSON PLANS", icon: BookOpen, requiredTier: 1 },
    { to: "/dashboard/students", label: "MY STUDENTS", icon: Users, requiredTier: 2 },
    { to: "/dashboard/classes", label: "MY CLASSES", icon: LayoutGrid, requiredTier: 2 },
    { to: "/dashboard/assessment", label: "ASSESSMENT", icon: GraduationCap, requiredTier: 1 },
    { to: "/dashboard/resources", label: "RESOURCES", icon: FileText, requiredTier: 1 },
    { to: "/dashboard/utilities/search", label: "QUICK SEARCH", icon: Search, requiredTier: 1 },
    { to: "/dashboard/agent", label: "AI AGENT", icon: Bot, requiredTier: 2 },
    { to: "/dashboard/utilities", label: "UTILITIES", icon: Wrench, requiredTier: 1 },
    { to: "/dashboard/billing", label: "BILLING", icon: CreditCard, requiredTier: 1 },
    { to: "/dashboard/account", label: "MY ACCOUNT", icon: UserCircle, requiredTier: 1 },
  ];

  const links = allLinks.filter(link => isTier2 ? true : link.requiredTier === 1);

  return (
    <Sidebar collapsible="icon" className="w-[--sidebar-width] group-data-[state=collapsed]:w-[--sidebar-width-icon] border-r border-gray-100 dark:border-white/5 bg-white dark:bg-[#020617] shadow-none transition-all duration-300">
      <SidebarHeader className="overflow-hidden p-6">
        <div className="group-data-[state=collapsed]:hidden transition-all duration-300">
          <div className="flex items-baseline gap-1">
            <span className="font-serif italic lowercase tracking-tight text-2xl dark:text-white text-black">sonoma</span>
            <span className="font-bold tracking-[0.2em] text-[8px] text-blue-500 uppercase">Workspace</span>
          </div>
          <p className="text-[7px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5 leading-none">Independent Platform</p>
        </div>
        <div className="group-data-[state=expanded]:hidden flex justify-center">
           <span className="font-serif italic text-xl text-blue-500">s</span>
        </div>
      </SidebarHeader>
      <SidebarContent className="px-3 pt-2 overflow-y-auto">
        <SidebarMenu className="space-y-1">
          {links.map((link) => {
             const isActive = pathname === link.to
               || (link.to === '/dashboard' && (pathname === '/dashboard/' || pathname === '/dashboard'))
               || (link.to === '/dashboard/utilities' && pathname.startsWith('/dashboard/utilities/') && !pathname.startsWith('/dashboard/utilities/search'))
               || (link.to !== '/dashboard' && link.to !== '/dashboard/utilities' && pathname.startsWith(link.to + '/'));
             return (
              <SidebarMenuItem key={link.to}>
                <SidebarMenuButton 
                  asChild 
                  isActive={isActive} 
                  className={`h-11 rounded-none transition-all duration-300 ${
                    isActive 
                      ? 'bg-black text-white pointer-events-none' 
                      : 'text-gray-500 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <Link to={link.to} onClick={() => { if (isMobile) setOpenMobile(false); }} className="flex items-center gap-3 w-full text-[10px] font-bold tracking-widest group/link">
                    <link.icon className="shrink-0" size={16} />
                    <span className="group-data-[state=collapsed]:hidden transition-all duration-300">{link.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
             );
          })}
        </SidebarMenu>


      </SidebarContent>
      <SidebarFooter className="p-6">
         <SidebarMenu>
          <SidebarMenuItem>
             <SidebarMenuButton 
               onClick={onSignOut}
               className="h-12 rounded-none text-gray-400 hover:text-black dark:hover:text-white hover:bg-red-50 dark:hover:bg-red-950/20 text-[10px] font-black uppercase tracking-[0.3em] border border-transparent hover:border-red-100 dark:hover:border-red-900/30 transition-all group/signout"
             >
                <LogOut size={18} className="shrink-0 transition-colors duration-300 group-hover/signout:text-red-600 dark:group-hover/signout:text-red-400" />
                <span className="group-data-[state=collapsed]:hidden transition-all duration-300">Sign Out</span>
             </SidebarMenuButton>
          </SidebarMenuItem>
         </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default WorkspaceSidebar;
